import React from 'react';
import { ArrowRight, CheckCircle2, Zap, Gift, Star, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { usePreApproval } from '../../pages/LandingPage';

const CoOpLeadMagnet = () => {
  const { openPreApproval } = usePreApproval();
  
  const coveredServices = [
    'Websites & Landing Pages',
    'Funnels & Automations',
    'AI Agents & Voice Bots',
    'CRM Setup & Support',
    'All Design & Copy Services',
    'Paid Ads Management',
    'Email Marketing',
    'SEO & Maps Optimization'
  ];

  const howItWorks = [
    { step: 1, title: 'Complete Pre-Approval', desc: 'Quick 3-minute qualification' },
    { step: 2, title: 'Book Your Closing Call', desc: 'Guided, no-pressure conversation' },
    { step: 3, title: 'Get Your $120K Co-Op Funds', desc: 'Funds deposited to your account' },
    { step: 4, title: 'Start Building', desc: 'Use on any DFY or DWY service!' }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-full px-5 py-2 mb-6">
            <Gift className="w-5 h-5 text-emerald-400" />
            <span className="text-white font-semibold">Business Co-Op Funding</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Get <span className="text-emerald-400">$120,000</span> in Co-Op Funds
          </h2>
          
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto">
            Get qualified for your <span className="text-emerald-400 font-semibold">$120,000 Co-Op Funding</span> and unlock the complete 
            <span className="text-cyan-400 font-semibold"> AI Horse-Powered System</span> — ready to use on any DFY or DWY services!
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-16">
          {/* Left Side - Value Card */}
          <div className="order-2 lg:order-1">
            <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 relative overflow-hidden">
              {/* Glow */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl" />
              
              {/* Badge */}
              <div className="inline-block bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
                100% Covered by Co-Op
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Everything You Need to <span className="text-emerald-400">Dominate</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {coveredServices.map((service, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{service}</span>
                  </div>
                ))}
              </div>

              {/* Credit Display */}
              <div className="bg-slate-900/80 border border-emerald-500/30 rounded-xl p-6 text-center">
                <p className="text-slate-400 text-sm mb-2">Your Co-Op Funds</p>
                <p className="text-5xl md:text-6xl font-bold text-emerald-400 mb-2">$120,000</p>
                <p className="text-slate-500 text-sm">4% Funding Fee or 5% Easy Payment Plan</p>
              </div>
            </div>
          </div>

          {/* Right Side - CTA & How It Works */}
          <div className="order-1 lg:order-2 space-y-8">
            {/* Main CTA Card */}
            <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-2xl p-8 text-center">
              <Star className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">
                Start Your Path to Funding
              </h3>
              <p className="text-slate-300 mb-6">
                Complete your 3-minute pre-approval and see if you qualify for the $120K Co-Op Funding
              </p>
              <Button
                onClick={openPreApproval}
                size="lg"
                data-testid="get-qualified-btn"
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-bold px-8 py-6 text-lg rounded-xl transition-all hover:shadow-xl hover:shadow-emerald-500/30 group"
              >
                Get Qualified Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-slate-500 text-sm mt-3">No credit card required • Takes 3 minutes</p>
            </div>

            {/* How It Works */}
            <div>
              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-cyan-400" />
                How It Works
              </h4>
              <div className="space-y-3">
                {howItWorks.map((item) => (
                  <div key={item.step} className="flex items-center gap-4 bg-slate-800/40 rounded-xl p-4 border border-slate-700/50">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{item.title}</p>
                      <p className="text-slate-500 text-xs">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Trust Element */}
        <div className="flex flex-wrap items-center justify-center gap-8 pt-8 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-green-400" />
            <span className="text-slate-400 text-sm">256-bit SSL Encrypted</span>
          </div>
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-emerald-400" />
            <span className="text-slate-400 text-sm">AI Certified Platform</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-cyan-400" />
            <span className="text-slate-400 text-sm">Best of Best 2025</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoOpLeadMagnet;
